import cloneDeep from 'lodash/cloneDeep';
import logger from '../../../../utils/logger';
import { getMaxNegotiationRoundWithOffers, isAssignmentComplete } from './helpers.js';

/**
 * Generalized utility for updating negotiation object state
 * Handles updating CaseAssignmentInfo for multiple cases
 */

/**
 * Updates the negotiation object with specified changes for given cases
 * 
 * @param {Object} negotiationObj - The current negotiation object
 * @param {Array} casesToUpdate - Array of cases (rows) to update
 * @param {Object} options - Update options
 * @param {Object} options.assignmentInfoUpdates - Updates to CaseAssignmentInfo
 * @param {Object} options.caseFieldUpdates - Updates to case-level fields
 * @param {Object} options.negotiationsUpdates - Updates to Negotiations array (setLiveOfferToZero, townCancelRestoreAventineLiveOffer, liveOffer, addOffer, etc.)
 * @param {Object} options.courtfilesUpdates - Updates to Courtfiles fields (stage-specific)
 * 
 * @returns {Object} Updated negotiation object
 */

export const updateNegotiationObject = (negotiationObj, casesToUpdate, options = {}) => {
  const { 
    assignmentInfoUpdates = {}, 
    caseFieldUpdates = {},
    negotiationsUpdates = {},
    courtfilesUpdates = {}
  } = options;
  
  const updatedNegotiationObj = cloneDeep(negotiationObj);
  
  casesToUpdate.forEach(caseItem => {
    const caseIndex = updatedNegotiationObj.cases.findIndex(
      (c) => c.parcel_id === caseItem.parcel_id
    );
    
    
    if (caseIndex !== -1) {
      const originalCase = updatedNegotiationObj.cases[caseIndex];
      
      // Handle CaseAssignmentInfo updates
      let updatedAssignmentInfo = originalCase.CaseAssignmentInfo;
      if (Object.keys(assignmentInfoUpdates).length > 0) {
        updatedAssignmentInfo = updateCaseAssignmentInfo(
          originalCase.CaseAssignmentInfo,
          assignmentInfoUpdates
        );
      }
      
      // Handle negotiations updates
      let updatedNegotiations = originalCase.Negotiations;
      if (Object.keys(negotiationsUpdates).length > 0) {
        updatedNegotiations = updateNegotiationsArray(
          originalCase.Negotiations,
          negotiationsUpdates,
          originalCase
        );
      } else {
        // No updates needed - use existing negotiations
        updatedNegotiations = originalCase.Negotiations;
      } 
      
      // Handle Courtfiles updates
      let updatedCase = {
        ...originalCase,
        CaseAssignmentInfo: updatedAssignmentInfo,
        Negotiations: updatedNegotiations,
        ...(caseItem.caseFieldUpdates ?? caseFieldUpdates)
      };
      
      // Apply Courtfiles field updates if specified
      if (Object.keys(courtfilesUpdates).length > 0) {
        // Determine stage for this case (default to SCAR if not specified)
        const stage = caseItem.stage || caseItem.settlementDetails?.stage || 'SCAR';
        
        // Clear only the fields for the specific stage
        const stageFieldsToClear = {
          [`${stage}DeterminationAction`]: null,
          [`${stage}DeterminationValue`]: null,
          [`${stage}SettleDate`]: null,
          Negotiator: null
        };
        
        // Apply the Courtfiles updates to the case
        updatedCase = {
          ...updatedCase,
          ...stageFieldsToClear
        };
        
        // logger.log(`🔍 [NEGOTIATION UPDATER] Clearing Courtfiles fields for ${stage}:`, stageFieldsToClear);
      }
      
      // Update the case in the negotiation object
      updatedNegotiationObj.cases[caseIndex] = updatedCase;
    }
  });
  
  return updatedNegotiationObj;
};

/**
 * Updates CaseAssignmentInfo based on the provided parameters
 * 
 * @param {Array} existingAssignmentInfo - Current CaseAssignmentInfo array
 * @param {Object} options - Update options
 * @param {Array} options.removeReviewTypes - Review types to remove from assignment info
 * @param {Array} options.updateReviewTypes - Review types to update with new data
 * @param {Array} options.addAssignments - New assignments to add
 * 
 * @returns {Array} Updated CaseAssignmentInfo array
 */
const updateCaseAssignmentInfo = (existingAssignmentInfo, options = {}) => {
  const { 
    removeReviewTypes = [], 
    updateReviewTypes = [], 
    addAssignments = [] 
  } = options;
  
  let updatedInfo = Array.isArray(existingAssignmentInfo) 
    ? [...existingAssignmentInfo] 
    : [];
  
  // Remove specified review types
  if (removeReviewTypes.length > 0) {
    updatedInfo = updatedInfo.filter(info => 
      !removeReviewTypes.includes(info.ReviewType)
    );
  }
  
  // Update existing review types with new data
  if (updateReviewTypes.length > 0) {
    updatedInfo = updatedInfo.map(info => {
      const updateForThisType = updateReviewTypes.find(
        update => update.ReviewType === info.ReviewType
      );
      
      if (updateForThisType) {
        return {
          ...info,
          ...updateForThisType.updates
        };
      }
      
      return info;
    });
  }
  
  // Add new assignments
  if (addAssignments.length > 0) {
    updatedInfo = [
      ...updatedInfo,
      ...addAssignments
    ];
  }
  
  return updatedInfo;
};



// a totally HEINOUS legacy function. A lot of this logic is duplicated in the backend.
/**
 * Updates the Negotiations array based on the provided parameters
 * 
 * @param {Array} existingNegotiations - Current Negotiations array
 * @param {Object} options - Update options
 * @param {number} options.setLiveOfferToZero - Set LiveOffer to 0 for Aventine offers
 * @param {boolean} options.townCancelRestoreAventineLiveOffer - Latest round: clear Aventine Response, set LiveOffer to 1 (town settlement cancel)
 * @param {number} options.liveOffer - Set LiveOffer to specified value for Aventine offers
 * 
 * @returns {Array} Updated Negotiations array
 */
const updateNegotiationsArray = (existingNegotiations, options = {}, caseItem = null) => {
  const {
    setLiveOfferToZero = false,
    townCancelRestoreAventineLiveOffer = false,
    liveOffer,
    addNewNegotiation,
    addOffer,
    updateOffer,
  } = options;
  
  // logger.log('🔍 [NEGOTIATION UPDATER] Function called with:', {
  //   existingNegotiations,
  //   options,
  //   caseItem: caseItem ? { parcel_id: caseItem.parcel_id, CaseAssignmentStatus: caseItem.CaseAssignmentStatus } : null
  // });
  
  // Check if case is finalized (CaseReview === true)
  const isCaseFinalized = isAssignmentComplete(caseItem, 'CaseReview');
  // logger.log('🔍 [NEGOTIATION UPDATER] Case finalized status:', isCaseFinalized);
  
  if (!Array.isArray(existingNegotiations)) {
    logger.log('🔍 [NEGOTIATION UPDATER] Existing negotiations is not an array, returning as-is');
    return existingNegotiations;
  }
  
  let updatedNegotiations = [...existingNegotiations];
  // logger.log('🔍 [NEGOTIATION UPDATER] Starting with negotiations:', updatedNegotiations);
  
  // Add or update offer - respect round parameter if specified, otherwise use existing behavior
  if (addOffer && caseItem) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing addOffer branch');
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing addOffer:', addOffer);
    // logger.log('🔍 [NEGOTIATION UPDATER] Case item:', caseItem);
    // logger.log('🔍 [NEGOTIATION UPDATER] Existing negotiations:', existingNegotiations);
    
    const { offeredBy, offerValue, round, response, comments, offerId } = addOffer;
    // logger.log('🔍 [NEGOTIATION UPDATER] Extracted addOffer properties:', { offeredBy, offerValue, round, response, comments, offerId });
    
    
    if (round !== undefined && round !== null) {
      // logger.log('🔍 [NEGOTIATION UPDATER] Round is specified, processing round-specific logic');
      // Round is explicitly specified - target that specific round
      const targetRound = round;
      let targetRoundIndex = updatedNegotiations.findIndex(negotiation => negotiation.round === targetRound);
      
      // logger.log('🔍 [NEGOTIATION UPDATER] Round specified:', targetRound);
      // logger.log('🔍 [NEGOTIATION UPDATER] Target round index:', targetRoundIndex);
      
      if (targetRoundIndex === -1) {
        // Round doesn't exist - create it
        // logger.log('🔍 [NEGOTIATION UPDATER] Creating new round:', targetRound);
        
        const newOffer = {
          OfferedBy: offeredBy,
          Offer: offerValue !== null ? Number(offerValue) : null,
          OfferDate: new Date().toISOString(),
          Response: response,
          Comments: comments || null,
          LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
          OfferID: offerId || null // Use OfferID from backend if available
        };
        
        // logger.log('🔍 [NEGOTIATION UPDATER] New offer created:', newOffer);
        
        updatedNegotiations.push({
          round: targetRound,
          offers: [newOffer]
        });
        
        // logger.log('🔍 [NEGOTIATION UPDATER] Updated negotiations after push:', updatedNegotiations);
      } else {
        // Round exists - update or add offer
        const existingOfferIndex = updatedNegotiations[targetRoundIndex].offers.findIndex(offer => offer.OfferedBy === offeredBy);
        
        if (existingOfferIndex !== -1) {
          // Check if we're updating from "Withdraw" to a number - change response to "Counter"
          const existingOffer = updatedNegotiations[targetRoundIndex].offers[existingOfferIndex];
          const wasWithdraw = existingOffer.Response && existingOffer.Response.toLowerCase() === 'withdraw';
          const isUpdatingToNumber = offerValue !== null && offerValue !== '' && !isNaN(Number(offerValue)) && Number(offerValue) > 0;
          
          let newResponse = response || existingOffer.Response || null;
          if (wasWithdraw && isUpdatingToNumber) {
            newResponse = 'Counter';
          }
          
          // Update existing offer in target round
          updatedNegotiations[targetRoundIndex].offers[existingOfferIndex] = {
            ...existingOffer,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: newResponse,
            Comments: comments !== undefined ? comments : existingOffer.Comments,
            LiveOffer: existingOffer.LiveOffer  // Preserve existing LiveOffer value
          };
        } else {
          // Add new offer to target round
          updatedNegotiations[targetRoundIndex].offers.push({
            OfferedBy: offeredBy,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: response,
            Comments: comments || null,
            LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
            OfferID: offerId || null // Use OfferID from backend if available
          });
        }
      }
    } else {
      // logger.log('🔍 [NEGOTIATION UPDATER] No round specified, using existing behavior');
      // No round specified - use existing behavior (most recent round logic)
      if (updatedNegotiations.length === 0) {
        // logger.log('🔍 [NEGOTIATION UPDATER] No existing negotiations, creating first round');
        // No existing negotiations - create first round
        updatedNegotiations.push({
          round: 1,
          offers: [{
            OfferedBy: offeredBy,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: response,
            Comments: comments || null,
            LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
            OfferID: offerId || null // Use OfferID from backend if available
          }]
        });
        // logger.log('🔍 [NEGOTIATION UPDATER] Created first round:', updatedNegotiations);
      } else {
        // logger.log('🔍 [NEGOTIATION UPDATER] Existing negotiations found, processing most recent round logic');
        // Find the most recent round that has the same round number as the last entry
        // This handles cases where there might be multiple negotiation entries
        const lastRound = updatedNegotiations[updatedNegotiations.length - 1];
        const currentRoundNumber = lastRound.round;
        
        // Check if this is a new round scenario (Aventine updating after Town responded)
        // Only create new round if:
        // 1. It's Aventine updating
        // 2. Town has a non-zero offer in the current round
        // 3. Aventine already has an offer in the current round (meaning Town responded to it)
        const hasTownOffer = lastRound.offers.some(offer => 
          offer.OfferedBy === 'Town' && Number(offer.Offer) > 0
        );
        const hasAventineOffer = lastRound.offers.some(offer => 
          offer.OfferedBy === 'Aventine'
        );
        
        if (offeredBy === 'Aventine' && hasTownOffer && hasAventineOffer) {
          // Aventine updating after Town responded - create new round
          const newRound = currentRoundNumber + 1;
          updatedNegotiations.push({
            round: newRound,
            offers: [{
              OfferedBy: offeredBy,
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: response,
              Comments: comments || null,
              LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
              OfferID: offerId || null // Use OfferID from backend if available
            }]
          });
        } else {
          // Add to current round
          const existingOfferIndex = lastRound.offers.findIndex(offer => offer.OfferedBy === offeredBy);
          
          if (existingOfferIndex !== -1) {
            // Update existing offer
            lastRound.offers[existingOfferIndex] = {
              ...lastRound.offers[existingOfferIndex],
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: response,
              Comments: comments !== undefined ? comments : lastRound.offers[existingOfferIndex].Comments,
              LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
              OfferID: offerId || null // Use OfferID from backend if available
            };
          } else {
            // Add new offer to current round
            lastRound.offers.push({
              OfferedBy: offeredBy,
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: response,
              Comments: comments || null,
              LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
              OfferID: offerId || null // Use OfferID from backend if available
            });
          }
        }
      }
    }
    // logger.log('🔍 [NEGOTIATION UPDATER] After addOffer processing:', updatedNegotiations);
  }
  // Update existing offer by OfferID
  else if (updateOffer && caseItem) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing updateOffer branch');
    const { offerId, offeredBy, offerValue, response, comments } = updateOffer;
    // logger.log('🔍 [NEGOTIATION UPDATER] Update offer details:', { offerId, offeredBy, offerValue, response, comments });
    
    // Find and update the offer with the specified OfferID
    for (let i = 0; i < updatedNegotiations.length; i++) {
      const round = updatedNegotiations[i];
      if (round.offers && Array.isArray(round.offers)) {
        const offerIndex = round.offers.findIndex(offer => offer.OfferID === offerId);
        if (offerIndex !== -1) {
          // logger.log('🔍 [NEGOTIATION UPDATER] Found offer to update at index:', offerIndex);
          // Check if we're updating from "Withdraw" to a number - change response to "Counter"
          const existingOffer = updatedNegotiations[i].offers[offerIndex];
          const wasWithdraw = existingOffer.Response && existingOffer.Response.toLowerCase() === 'withdraw';
          const isUpdatingToNumber = offerValue !== null && offerValue !== '' && !isNaN(Number(offerValue)) && Number(offerValue) > 0;

          let newResponse = response || existingOffer.Response || 'Pending';
          if (wasWithdraw && isUpdatingToNumber) {
            newResponse = 'Counter';
          }
          
          // Update the existing offer
          updatedNegotiations[i].offers[offerIndex] = {
            ...existingOffer,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: newResponse,
            Comments: comments !== undefined ? comments : existingOffer.Comments,
            LiveOffer: existingOffer.OfferedBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1
          };
          // logger.log('🔍 [NEGOTIATION UPDATER] Updated offer:', updatedNegotiations[i].offers[offerIndex]);
          break; // Found and updated, exit the loop
        }
      }
    }
    // logger.log('🔍 [NEGOTIATION UPDATER] After updateOffer processing:', updatedNegotiations);
  }
  else if (townCancelRestoreAventineLiveOffer) {
    const maxRound = getMaxNegotiationRoundWithOffers(updatedNegotiations);
    if (maxRound > 0) {
      updatedNegotiations = updatedNegotiations.map((negotiation) => {
        if (negotiation.round !== maxRound || !negotiation.offers?.length) {
          return negotiation;
        }
        return {
          ...negotiation,
          offers: negotiation.offers.map((offer) =>
            offer.OfferedBy === 'Aventine'
              ? { ...offer, Response: null, LiveOffer: 1 }
              : offer
          ),
        };
      });
    }
  }
  // Add new negotiation entry (for sending offers)
  else if (addNewNegotiation && caseItem) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing addNewNegotiation branch');
    // Only create offers that have actual values
    const offers = [];
    
    // Add Aventine offer if it has a value
    // if (caseItem.OfferValue && Number(caseItem.OfferValue) > 0) {
      offers.push({
        OfferedBy: 'Aventine',
        Offer: Number(caseItem.OfferValue),
        OfferDate: new Date().toISOString(),
        LiveOffer: liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0),
        OfferID: null // Will be updated when backend returns the actual OfferID
      });
    // }
    
    // Add Town offer only if it has a value
    // Do you need to update these zero checks??
    // if ((caseItem.TownOffer && Number(caseItem.TownOffer) > 0) ) {
      offers.push({
        OfferedBy: 'Town',
        Offer: Number(caseItem.TownOffer),
        OfferDate: new Date().toISOString(),
        LiveOffer: 0,
        OfferID: null // Will be updated when backend returns the actual OfferID
      });
    // }
    
    //  logger.log('🔍 [NEGOTIATION UPDATER] Created offers for addNewNegotiation:', offers);
    
    // Only create negotiation entry if there are actual offers
    if (offers.length > 0) {
      const newNegotiation = {
        round: 1,
        offers: offers
      };
      
      updatedNegotiations.push(newNegotiation);
      // logger.log('🔍 [NEGOTIATION UPDATER] Added new negotiation:', newNegotiation);
    } else {
      logger.log('🔍 [NEGOTIATION UPDATER] No offers to add, skipping negotiation creation');
    }
    // logger.log('🔍 [NEGOTIATION UPDATER] After addNewNegotiation processing:', updatedNegotiations);
  }
  // Set LiveOffer to 0 for Aventine offers (for canceling offers)
  else if (setLiveOfferToZero) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing setLiveOfferToZero branch');
    updatedNegotiations = updatedNegotiations.map((negotiation, negotiationIndex) => {
      if (negotiation.offers && Array.isArray(negotiation.offers)) {
        const updatedOffers = negotiation.offers.map((offer, offerIndex) => {
          if (offer.OfferedBy === 'Aventine') {
            return {
              ...offer,
              LiveOffer: 0
            };
          }
          return offer;
        });
        
        const updatedNegotiation = {
          ...negotiation,
          offers: updatedOffers
        };
        
        return updatedNegotiation;
      } else {
        return negotiation;
      }
    });
    // logger.log('🔍 [NEGOTIATION UPDATER] After setLiveOfferToZero processing:', updatedNegotiations);
  } 
  // Set LiveOffer to specified value for Aventine offers (for sending offers)
  else if (liveOffer === 1) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing liveOffer === 1 branch');
    updatedNegotiations = updatedNegotiations.map((negotiation, negotiationIndex) => {
      if (negotiation.offers && Array.isArray(negotiation.offers)) {
        const updatedOffers = negotiation.offers.map((offer, offerIndex) => {
          if (offer.OfferedBy === 'Aventine') {
            return {
              ...offer,
              LiveOffer: liveOffer
            };
          }
          return offer;
        });
        
        const updatedNegotiation = {
          ...negotiation,
          offers: updatedOffers
        };
        
        return updatedNegotiation;
      } else {
        return negotiation;
      }
    });
    // logger.log('🔍 [NEGOTIATION UPDATER] After liveOffer === 1 processing:', updatedNegotiations);
  }
  // Add or update offer - respect round parameter if specified, otherwise use existing behavior
  else if (addOffer && caseItem) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing addOffer branch');
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing addOffer:', addOffer);
    // logger.log('🔍 [NEGOTIATION UPDATER] Case item:', caseItem);
    // logger.log('🔍 [NEGOTIATION UPDATER] Existing negotiations:', existingNegotiations);
    
    const { offeredBy, offerValue, round, response, comments, offerId } = addOffer;
    // logger.log('🔍 [NEGOTIATION UPDATER] Extracted addOffer properties:', { offeredBy, offerValue, round, response, comments, offerId });
    
    
    if (round !== undefined && round !== null) {
      // logger.log('🔍 [NEGOTIATION UPDATER] Round is specified, processing round-specific logic');
      // Round is explicitly specified - target that specific round
      const targetRound = round;
      let targetRoundIndex = updatedNegotiations.findIndex(negotiation => negotiation.round === targetRound);
      
      // logger.log('🔍 [NEGOTIATION UPDATER] Round specified:', targetRound);
      // logger.log('🔍 [NEGOTIATION UPDATER] Target round index:', targetRoundIndex);
      
      if (targetRoundIndex === -1) {
        // Round doesn't exist - create it
        // logger.log('🔍 [NEGOTIATION UPDATER] Creating new round:', targetRound);
        
        const newOffer = {
          OfferedBy: offeredBy,
          Offer: offerValue !== null ? Number(offerValue) : null,
          OfferDate: new Date().toISOString(),
          Response: response,
          Comments: comments || null,
          LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
          OfferID: offerId || null // Use OfferID from backend if available
        };
        
        // logger.log('🔍 [NEGOTIATION UPDATER] New offer created:', newOffer);
        
        updatedNegotiations.push({
          round: targetRound,
          offers: [newOffer]
        });
        
        // logger.log('🔍 [NEGOTIATION UPDATER] Updated negotiations after push:', updatedNegotiations);
      } else {
        // Round exists - update or add offer
        const existingOfferIndex = updatedNegotiations[targetRoundIndex].offers.findIndex(offer => offer.OfferedBy === offeredBy);
        
        if (existingOfferIndex !== -1) {
          // Check if we're updating from "Withdraw" to a number - change response to "Counter"
          const existingOffer = updatedNegotiations[targetRoundIndex].offers[existingOfferIndex];
          const wasWithdraw = existingOffer.Response && existingOffer.Response.toLowerCase() === 'withdraw';
          const isUpdatingToNumber = offerValue !== null && offerValue !== '' && !isNaN(Number(offerValue)) && Number(offerValue) > 0;
          
          let newResponse = response || existingOffer.Response || null;
          if (wasWithdraw && isUpdatingToNumber) {
            newResponse = 'Counter';
          }
          
          // Update existing offer in target round
          updatedNegotiations[targetRoundIndex].offers[existingOfferIndex] = {
            ...existingOffer,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: newResponse,
            Comments: comments !== undefined ? comments : existingOffer.Comments,
            LiveOffer: existingOffer.LiveOffer  // Preserve existing LiveOffer value
          };
        } else {
          // Add new offer to target round
          updatedNegotiations[targetRoundIndex].offers.push({
            OfferedBy: offeredBy,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: response,
            Comments: comments || null,
            LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
            OfferID: offerId || null // Use OfferID from backend if available
          });
        }
      }
    } else {
      // logger.log('🔍 [NEGOTIATION UPDATER] No round specified, using existing behavior');
      // No round specified - use existing behavior (most recent round logic)
      if (updatedNegotiations.length === 0) {
        // logger.log('🔍 [NEGOTIATION UPDATER] No existing negotiations, creating first round');
        // No existing negotiations - create first round
        updatedNegotiations.push({
          round: 1,
          offers: [{
            OfferedBy: offeredBy,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: response,
            Comments: comments || null,
            LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
            OfferID: offerId || null // Use OfferID from backend if available
          }]
        });
        // logger.log('🔍 [NEGOTIATION UPDATER] Created first round:', updatedNegotiations);
      } else {
        // logger.log('🔍 [NEGOTIATION UPDATER] Existing negotiations found, processing most recent round logic');
        // Find the most recent round that has the same round number as the last entry
        // This handles cases where there might be multiple negotiation entries
        const lastRound = updatedNegotiations[updatedNegotiations.length - 1];
        const currentRoundNumber = lastRound.round;
        
        // Check if this is a new round scenario (Aventine updating after Town responded)
        // Only create new round if:
        // 1. It's Aventine updating
        // 2. Town has a non-zero offer in the current round
        // 3. Aventine already has an offer in the current round (meaning Town responded to it)
        const hasTownOffer = lastRound.offers.some(offer => 
          offer.OfferedBy === 'Town' && Number(offer.Offer) > 0
        );
        const hasAventineOffer = lastRound.offers.some(offer => 
          offer.OfferedBy === 'Aventine'
        );
        
        if (offeredBy === 'Aventine' && hasTownOffer && hasAventineOffer) {
          // Aventine updating after Town responded - create new round
          const newRound = lastRound.round + 1;
          
          updatedNegotiations.push({
            round: newRound,
            offers: [{
              OfferedBy: 'Aventine',
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: response,
              Comments: comments || null,
              LiveOffer: liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)
            }]
          });
        } else {
          // Update existing offer in current round or add new offer
          const existingOfferIndex = lastRound.offers.findIndex(offer => offer.OfferedBy === offeredBy);
          
          if (existingOfferIndex !== -1) {
            // Check if we're updating from "Withdraw" to a number - change response to "Counter"
            const existingOffer = lastRound.offers[existingOfferIndex];
            const wasWithdraw = existingOffer.Response && existingOffer.Response.toLowerCase() === 'withdraw';
            const isUpdatingToNumber = offerValue !== null && offerValue !== '' && !isNaN(Number(offerValue)) && Number(offerValue) > 0;
            
            let newResponse = response || existingOffer.Response || 'Pending';
            if (wasWithdraw && isUpdatingToNumber) {
              newResponse = 'Counter';
            }
            
            // Update existing offer in current round
            lastRound.offers[existingOfferIndex] = {
              ...existingOffer,
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: newResponse,
              Comments: comments !== undefined ? comments : existingOffer.Comments,
              LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1
            };
          } else {
            // Add new offer to current round
            lastRound.offers.push({
              OfferedBy: offeredBy,
              Offer: offerValue !== null ? Number(offerValue) : null,
              OfferDate: new Date().toISOString(),
              Response: response,
              Comments: comments || null,
              LiveOffer: offeredBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1,
              OfferID: offerId || null // Use OfferID from backend if available
            });
          }
        }
      }
    }
  }
  // Update existing offer by OfferID
  else if (updateOffer && caseItem) {
    // logger.log('🔍 [NEGOTIATION UPDATER] Processing updateOffer branch');
    const { offerId, offeredBy, offerValue, response, comments } = updateOffer;
    // logger.log('🔍 [NEGOTIATION UPDATER] Update offer details:', { offerId, offeredBy, offerValue, response, comments });
    
    // Find and update the offer with the specified OfferID
    for (let i = 0; i < updatedNegotiations.length; i++) {
      const round = updatedNegotiations[i];
      if (round.offers && Array.isArray(round.offers)) {
        const offerIndex = round.offers.findIndex(offer => offer.OfferID === offerId);
        if (offerIndex !== -1) {
          // logger.log('🔍 [NEGOTIATION UPDATER] Found offer to update at index:', offerIndex);
          // Check if we're updating from "Withdraw" to a number - change response to "Counter"
          const existingOffer = updatedNegotiations[i].offers[offerIndex];
          const wasWithdraw = existingOffer.Response && existingOffer.Response.toLowerCase() === 'withdraw';
          const isUpdatingToNumber = offerValue !== null && offerValue !== '' && !isNaN(Number(offerValue)) && Number(offerValue) > 0;

          let newResponse = response || existingOffer.Response || 'Pending';
          if (wasWithdraw && isUpdatingToNumber) {
            newResponse = 'Counter';
          }
          
          // Update the existing offer
          updatedNegotiations[i].offers[offerIndex] = {
            ...existingOffer,
            Offer: offerValue !== null ? Number(offerValue) : null,
            OfferDate: new Date().toISOString(),
            Response: newResponse,
            Comments: comments !== undefined ? comments : existingOffer.Comments,
            LiveOffer: existingOffer.OfferedBy === 'Aventine' ? (liveOffer !== undefined ? liveOffer : (isCaseFinalized ? 1 : 0)) : 1
          };
          // logger.log('🔍 [NEGOTIATION UPDATER] Updated offer:', updatedNegotiations[i].offers[offerIndex]);
          break; // Found and updated, exit the loop
        }
      }
    }
    // logger.log('🔍 [NEGOTIATION UPDATER] After updateOffer processing:', updatedNegotiations);
  } else {
    // logger.log('🔍 [NEGOTIATION UPDATER] No matching condition found, returning negotiations unchanged');
    // logger.log('🔍 [NEGOTIATION UPDATER] Available options:', { setLiveOfferToZero, liveOffer, addNewNegotiation, addOffer, updateOffer });
    // logger.log('🔍 [NEGOTIATION UPDATER] Case item available:', !!caseItem);
  }
  
  
  // logger.log('🔍 [NEGOTIATION UPDATER] Final updated negotiations:', updatedNegotiations);
  return updatedNegotiations;
};

/**
 * Helper function to create a new assignment entry
 * 
 * @param {string} reviewType - The review type (e.g., 'Negotiation', 'CaseReview')
 * @param {string} assignee - User ID of the assignee
 * @param {Object} additionalFields - Additional fields to include
 * 
 * @returns {Object} New assignment object
 */
export const createAssignmentEntry = (reviewType, assignee, additionalFields = {}) => {
  return {
    ReviewType: reviewType,
    Assignee: assignee,
    DateAssigned: new Date().toISOString().slice(0, 19).replace('T', ' '),
    DateCompleted: null,
    MiscDesc: null,
    ...additionalFields
  };
};


/**
 * Example usage:
 * 
 * // Cancel offers - remove negotiation, clear case review completion, set LiveOffer to 0
 * const updatedObj = updateNegotiationObject(
 *   negotiationObj, 
 *   selectedCases, 
 *   {
 *     assignmentStatusUpdates: {
 *       Negotiation: null,
 *       CaseReview: false
 *     },
 *     assignmentInfoUpdates: {
 *       removeReviewTypes: ['Negotiation'],
 *       updateReviewTypes: [
 *         {
 *           ReviewType: 'CaseReview',
 *           updates: { DateCompleted: null }
 *         }
 *       ]
 *     },
 *     negotiationsUpdates: {
 *       setLiveOfferToZero: true
 *     }
 *   }
 * );
 * 
 * // Send offers (start negotiation)
 * const updatedObj = updateNegotiationObject(
 *   negotiationObj, 
 *   selectedCases, 
 *   {
 *     assignmentStatusUpdates: {
 *       Negotiation: true
 *     },
 *     assignmentInfoUpdates: {
 *       removeReviewTypes: ['Negotiation'],
 *       addAssignments: [
 *         createAssignmentEntry('Negotiation', currentUserId)
 *       ]
 *     },
 *     negotiationsUpdates: {
 *       liveOffer: 1
 *     }
 *   }
 * );
 * 
 * // Simple status update only
 * const updatedObj = updateNegotiationObject(
 *   negotiationObj, 
 *   selectedCases, 
 *   {
 *     assignmentStatusUpdates: {
 *       CaseReview: true
 *     }
 *   }
 * );
 * 
 * // Update case-level fields
 * const updatedObj = updateNegotiationObject(
 *   negotiationObj, 
 *   selectedCases, 
 *   {
 *     caseFieldUpdates: {
 *       OfferValue: 50000,
 *       Status: 'Updated'
 *     }
 *   }
 * );
 * 
 * // Cancel offers - clear Courtfiles fields for specific stage
 * const updatedObj = updateNegotiationObject(
 *   negotiationObj, 
 *   selectedCases, 
 *   {
 *     assignmentStatusUpdates: {
 *       Negotiation: false
 *     },
 *     negotiationsUpdates: {
 *       setLiveOfferToZero: true
 *     },
 *     courtfilesUpdates: {
 *       clearStageFields: true  // Clears stage-specific determination fields
 *     }
 *   }
 * );
 */
